/**
 * Meta Pixel (Facebook Pixel) Service
 * Handles initialization and event tracking for Meta Pixel
 */

declare global {
  interface Window {
    fbq: {
      (...args: any[]): void;
      loaded?: boolean;
      callMethod?: (...args: any[]) => void;
      queue?: any[];
      push?: (...args: any[]) => void;
      version?: string;
    };
    _fbq?: (...args: any[]) => void;
  }
}

const PIXEL_ID_ENV_KEY = 'REACT_APP_META_PIXEL_ID';

/**
 * Initialize Meta Pixel
 * This should be called once when the app loads
 */
export const initializeMetaPixel = (): void => {
  try {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
      return;
    }

    // Get Pixel ID from environment variable
    const pixelId = process.env[PIXEL_ID_ENV_KEY];
    
    if (!pixelId) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ Meta Pixel ID not found. Set REACT_APP_META_PIXEL_ID in your .env file');
      }
      return;
    }

    // Check if already initialized by checking if fbq is callable and has been set up
    if (typeof window.fbq === 'function' && (window.fbq as any).loaded) {
      if (process.env.NODE_ENV === 'development') {
        console.log('ℹ️ Meta Pixel already initialized');
      }
      return;
    }

    // Initialize fbq function
    (function(f: any, b: any, e: string, v: string, n?: any, t?: any, s?: any) {
      if (f.fbq) return;
      n = f.fbq = function() {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode?.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

    // Initialize the pixel
    window.fbq('init', pixelId);
    window.fbq('track', 'PageView');

    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Meta Pixel initialized with ID:', pixelId);
    }
  } catch (error) {
    // Silently handle errors to not affect user experience
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ Error initializing Meta Pixel:', error);
    }
  }
};

/**
 * Track a custom event
 * @param eventName - The name of the event to track
 * @param eventData - Optional event parameters
 */
export const trackMetaPixelEvent = (eventName: string, eventData?: Record<string, any>): void => {
  try {
    if (typeof window === 'undefined' || typeof window.fbq !== 'function' || !(window.fbq as any).loaded) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ Meta Pixel not initialized. Call initializeMetaPixel() first.');
      }
      return;
    }

    if (eventData) {
      window.fbq('track', eventName, eventData);
    } else {
      window.fbq('track', eventName);
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Meta Pixel event tracked:', eventName, eventData || '');
    }
  } catch (error) {
    // Silently handle errors
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ Error tracking Meta Pixel event:', error);
    }
  }
};

/**
 * Track page view
 */
export const trackPageView = (): void => {
  trackMetaPixelEvent('PageView');
};

/**
 * Track when a user views content
 */
export const trackViewContent = (contentName?: string, contentCategory?: string): void => {
  trackMetaPixelEvent('ViewContent', {
    content_name: contentName,
    content_category: contentCategory
  });
};

/**
 * Track when a user searches
 */
export const trackSearch = (searchString?: string): void => {
  trackMetaPixelEvent('Search', {
    search_string: searchString
  });
};

/**
 * Track when a user adds to cart
 */
export const trackAddToCart = (value?: number, currency?: string, contentName?: string): void => {
  trackMetaPixelEvent('AddToCart', {
    value: value,
    currency: currency || 'EGP',
    content_name: contentName
  });
};

/**
 * Track when a user initiates checkout
 */
export const trackInitiateCheckout = (value?: number, currency?: string, contentName?: string): void => {
  trackMetaPixelEvent('InitiateCheckout', {
    value: value,
    currency: currency || 'EGP',
    content_name: contentName
  });
};

/**
 * Track when a user completes a purchase
 */
export const trackPurchase = (value: number, currency?: string, contentName?: string): void => {
  trackMetaPixelEvent('Purchase', {
    value: value,
    currency: currency || 'EGP',
    content_name: contentName
  });
};

/**
 * Track when a user signs up
 */
export const trackCompleteRegistration = (method?: string): void => {
  trackMetaPixelEvent('CompleteRegistration', {
    method: method || 'email'
  });
};

/**
 * Track when a user starts a trial
 */
export const trackStartTrial = (value?: number, currency?: string): void => {
  trackMetaPixelEvent('StartTrial', {
    value: value,
    currency: currency || 'EGP'
  });
};

/**
 * Track when a user subscribes
 */
export const trackSubscribe = (value: number, currency?: string, planName?: string): void => {
  trackMetaPixelEvent('Subscribe', {
    value: value,
    currency: currency || 'EGP',
    content_name: planName
  });
};

/**
 * Track when a user contacts you
 */
export const trackContact = (): void => {
  trackMetaPixelEvent('Contact');
};

/**
 * Track when a user downloads something
 */
export const trackLead = (contentName?: string): void => {
  trackMetaPixelEvent('Lead', {
    content_name: contentName
  });
};

